import React from 'react';
import { MediaLibraryItem } from '../../../api/media';
import VideoThumbnail from './VideoThumbnail';
import {
  hasParent,
  mediaFolder,
  mediaDownloadName,
  mediaLabel,
  mediaStatus,
  resolveThumbSources,
  typeBadge,
} from './mediaCardUtils';

export interface MediaCardProps {
  item: MediaLibraryItem;
  selected: boolean;
  /** In `select` mode the checkbox is always visible (you're there to pick). */
  selectMode: boolean;
  /** Whether a favourite toggle is in flight for this item. */
  togglingFavourite: boolean;
  /** Primary activation: open the inspector (manage) or toggle the pick (select). */
  onActivate: (mediaId: string) => void;
  /** Explicit selection toggle (checkbox). */
  onToggleSelect: (mediaId: string) => void;
  onToggleFavourite: (mediaId: string, current: boolean) => void;
  /** Optional modifier-aware click (shift = range, cmd/ctrl = additive). */
  onClickWithModifiers?: (mediaId: string, e: React.MouseEvent | React.KeyboardEvent) => void;
  /** Hide the favourite affordance (e.g. single-select picker). Default true. */
  showFavourite?: boolean;
}

/**
 * The single, unified media cell — replaces BrowseTab's text row, the grouped
 * 80px square, and the picker row. A contact-sheet tile: cover-cropped webp
 * thumbnail over a dark ground, indigo selection frame + checkbox, amber
 * favourite, type/status badges, and a hover scrim carrying the label.
 *
 * Rendered as `role="gridcell"` + `role="button"` (not a real <button>, so the
 * nested checkbox/favourite <button>s stay valid HTML). Full keyboard model:
 * Enter/Space activate, the nested controls trap their own clicks.
 */
const MediaCardImpl: React.FC<MediaCardProps> = ({
  item,
  selected,
  selectMode,
  togglingFavourite,
  onActivate,
  onToggleSelect,
  onToggleFavourite,
  onClickWithModifiers,
  showFavourite = true,
}) => {
  const mediaId = item.media_id || item.id;
  const folder = mediaFolder(item);
  const status = mediaStatus(item);
  const label = mediaLabel(item);
  const badge = typeBadge(folder);
  // Pending media has no bytes yet. Failed media remains inspectable in manage
  // mode so its persistence diagnostics are visible, but is never selectable
  // as an input asset.
  const isActivationDisabled = status === 'generating' || (selectMode && status === 'failed');
  const isSelectionDisabled = status !== 'ready';
  const showLineage = hasParent(item);
  const downloadName = mediaDownloadName(item);

  const sources = React.useMemo(() => resolveThumbSources(item), [item]);
  const [imgFailed, setImgFailed] = React.useState(false);
  // Track whether the real thumbnail has painted, so the blur-up placeholder can
  // fade out underneath it once it has (and never flash an empty cell before).
  const [imgLoaded, setImgLoaded] = React.useState(false);
  React.useEffect(() => {
    setImgFailed(false);
    setImgLoaded(false);
  }, [sources.thumbnailUrl]);

  // Blur-up placeholder: a tiny base64 micro-thumb (P2) painted CSS-blurred
  // behind the cell. Absent → the neutral ground shows (today's behavior).
  const blurLayer = sources.microThumb ? (
    <div
      className="media-card-blur"
      data-covered={imgLoaded}
      style={{ backgroundImage: `url("${sources.microThumb}")` }}
      aria-hidden="true"
    />
  ) : null;

  const handleActivate = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (isActivationDisabled) return;
    if (onClickWithModifiers && 'shiftKey' in e && (e.shiftKey || e.metaKey || e.ctrlKey)) {
      onClickWithModifiers(mediaId, e);
      return;
    }
    onActivate(mediaId);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleActivate(e);
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      if (!isSelectionDisabled) onToggleSelect(mediaId);
    } else if ((e.key === 'f' || e.key === 'F') && showFavourite && !isSelectionDisabled) {
      e.preventDefault();
      onToggleFavourite(mediaId, !!item.is_favourite);
    }
  };

  // Thumbnail visual: image / video (poster or first-frame) / audio / fallback.
  // The blur-up placeholder (when present) sits behind the real media and fades
  // out once it loads, so cells never flash empty.
  const renderMedia = () => {
    if (folder === 'image' && sources.thumbnailUrl && !imgFailed) {
      return (
        <>
          {blurLayer}
          <img
            className="media-card-media"
            src={sources.thumbnailUrl}
            alt={label}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
          />
        </>
      );
    }
    if (folder === 'video') {
      if (sources.thumbnailUrl && !imgFailed) {
        return (
          <>
            {blurLayer}
            <img
              className="media-card-media"
              src={sources.thumbnailUrl}
              alt={label}
              loading="lazy"
              decoding="async"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgFailed(true)}
            />
          </>
        );
      }
      if (sources.videoUrl) {
        return (
          <div className="media-card-media" style={{ opacity: 1, animation: 'none' }}>
            <VideoThumbnail src={sources.videoUrl} />
          </div>
        );
      }
    }
    if (sources.thumbnailUrl && !imgFailed && folder !== 'audio') {
      return (
        <>
          {blurLayer}
          <img
            className="media-card-media"
            src={sources.thumbnailUrl}
            alt={label}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
          />
        </>
      );
    }
    return <div className="media-card-fallback">{badge.label}</div>;
  };

  return (
    <div
      role="button"
      tabIndex={isActivationDisabled ? -1 : 0}
      className="media-card"
      data-selected={selected}
      data-select-mode={selectMode}
      data-disabled={isActivationDisabled}
      aria-disabled={isActivationDisabled}
      aria-label={label}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      {renderMedia()}

      {/* selection checkbox */}
      <button
        type="button"
        className="media-card-check"
        data-checked={selected}
        aria-pressed={selected}
        aria-label={selected ? 'Deselect' : 'Select'}
        disabled={isSelectionDisabled}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(mediaId);
        }}
      >
        {selected ? '✓' : ''}
      </button>

      {/* favourite star */}
      {showFavourite && (
        <button
          type="button"
          className="media-card-fav"
          data-on={!!item.is_favourite}
          aria-pressed={!!item.is_favourite}
          disabled={togglingFavourite || isSelectionDisabled}
          title={item.is_favourite ? 'Remove from favourites' : 'Add to favourites'}
          onClick={(e) => {
            e.stopPropagation();
            if (isSelectionDisabled) return;
            onToggleFavourite(mediaId, !!item.is_favourite);
          }}
        >
          ★
        </button>
      )}

      {folder === 'image' && !selectMode && status === 'ready' && sources.fullUrl && (
        <a
          className="media-card-download"
          href={sources.fullUrl}
          download={downloadName}
          aria-label={`Download ${label}`}
          title="Download image"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          ↓
        </a>
      )}

      {/* lineage glyph (variation) */}
      {showLineage && (
        <span className="media-card-lineage" title="Variation — open to view lineage">
          ↳ VAR
        </span>
      )}

      {/* bottom scrim + label */}
      <div className="media-card-scrim">
        <p className="media-card-label">{label}</p>
      </div>

      {/* type badge */}
      <div className="media-card-badges">
        <span className={`media-badge ${badge.className}`}>{badge.label}</span>
      </div>

      {/* status overlay */}
      {status === 'generating' && (
        <div className="media-card-status media-badge-generating">generating</div>
      )}
      {status === 'failed' && (
        <div className="media-card-status media-badge-failed">failed</div>
      )}
    </div>
  );
};

// Memoize so toggling one card / typing in search re-renders only affected cells.
const MediaCard = React.memo(MediaCardImpl);
MediaCard.displayName = 'MediaCard';

export default MediaCard;
